import { Auth } from 'aws-amplify';
import { AxiosError } from 'axios';
import { action, makeObservable, observable, runInAction } from 'mobx';
import { Location } from 'react-router-dom';
import throttle from 'lodash/throttle';

import SignUpData from 'types/SignUpData';
import CookieService from './CookieService';
import EnvService from './EnvService';
import RdmApiService from './RdmApiService';
import User from 'types/User';
import GlobalErrorService from './GlobalErrorService';
import ConfirmationService from './ConfirmationService';
import { getCurrentUrl, getErrorMessage } from 'utils';
import UserJson from 'types/UserJson';
import UpdateProfileData from 'types/UpdateProfileData';
import { toBackendTier } from 'utils/tierMapping';

interface SearchState {
  text: string | null;
  limit: number;
  paginationToken: string | null;
  results: User[] | undefined;
  currentRequestNumber: number;
}

export default class AuthService {
  envService: EnvService;
  rdmApiService: RdmApiService;
  globalErrorService: GlobalErrorService;
  confirmationService: ConfirmationService;
  cookieService: CookieService;
  user: User | null | undefined = undefined;
  // email => user
  users: Record<string, User> = {};
  initialized: boolean = false;
  searchState: SearchState = {
    text: null,
    limit: 10,
    paginationToken: null,
    results: undefined,
    currentRequestNumber: 0,
  };

  constructor({
    envService,
    cookieService,
    rdmApiService,
    globalErrorService,
    confirmationService,
  }: {
    envService: EnvService;
    cookieService: CookieService;
    rdmApiService: RdmApiService;
    globalErrorService: GlobalErrorService;
    confirmationService: ConfirmationService;
  }) {
    this.envService = envService;
    this.cookieService = cookieService;
    this.rdmApiService = rdmApiService;
    this.confirmationService = confirmationService;
    this.globalErrorService = globalErrorService;
    makeObservable(this, {
      user: observable,
      initialized: observable,
      users: observable,
      searchState: observable,
      cacheUserIfNeeded: action,
      fetchUser: action,
      _searchUsers: action,
    });
  }

  get logInUrl(): string {
    return `${this.envService.getValue('DATA_REPO_URL')}/oauth/login/cognito/`;
  }

  get csrfToken(): string | null {
    return this.cookieService.getCookie('csrftoken');
  }

  get isLoggedIn(): boolean {
    return this.user !== undefined && this.user !== null;
  }

  initialize = async (): Promise<void> => {
    try {
      await this.autoLogIn();
      runInAction(() => (this.initialized = true));
    } catch (error: unknown) {
      this.globalErrorService.showError({
        message: getErrorMessage({
          error,
          defaultMessage: 'Could not initialize application.',
        }),
      });
    }
  };

  signUp = async (data: SignUpData): Promise<void> => {
    await Auth.signUp({
      username: data.email,
      password: data.password,
      attributes: {
        email: data.email,
        family_name: data.lastName,
        given_name: data.firstName,
        'custom:affiliation': data.affiliation,
        'custom:justification': data.justification,
        'custom:membership_tier': data.membershipTier,
        ...(data.orcidId ? { 'custom:orcid_id': data.orcidId } : {}),
      },
    });
  };

  signIn = async ({
    email,
    password,
  }: {
    email: string;
    password: string;
  }): Promise<void> => {
    await Auth.signIn({ username: email, password });
  };

  updateProfile = async (data: UpdateProfileData): Promise<void> => {
    await this.rdmApiService.axios.post('/current-msdlive-user', {
      email: data.email,
      family_name: data.lastName,
      given_name: data.firstName,
      'custom:affiliation': data.affiliation,
      ...(data.orcidId
        ? { 'custom:orcid_id': data.orcidId }
        : { 'custom:orcid_id': '' }),
    });
    await this.autoLogIn();
  };

  resendVerificationEmail = async ({
    email,
  }: {
    email: string;
  }): Promise<void> => {
    await Auth.resendSignUp(email);
  };

  verifyEmail = async ({
    email,
    code,
  }: {
    email?: string | null;
    code?: string | null;
  }): Promise<void> => {
    if (!email || !code) {
      throw new Error('Can not verify email.');
    }

    try {
      await Auth.confirmSignUp(email, code);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      if (
        error.message !==
        'User cannot be confirmed. Current status is CONFIRMED'
      ) {
        throw error;
      }
    }
  };

  autoLogIn = async (): Promise<void> => {
    try {
      const userResp = await this.rdmApiService.axios.get(
        '/current-msdlive-user'
      );

      const user = this.cacheUserIfNeeded(userResp.data);
      runInAction(() => {
        this.user = user;
      });
    } catch (error: unknown) {
      // don't throw the error, just log it and set user to null
      // otherwise when RDM is down and we want to show the "site down" page
      // it instead renders an error.
      console.error(`error in autoLogIn: ${error}`);
      runInAction(() => {
        this.user = null;
      });
    }
  };

  cacheUserIfNeeded = (userData: UserJson): User => {
    let user = this.getUser(userData.email);
    if (!user) {
      user = new User({
        userData,
        envService: this.envService,
        rdmApiService: this.rdmApiService,
        globalErrorService: this.globalErrorService,
        confirmationService: this.confirmationService,
      });
      this.users[user.email] = user;
    } else {
      user.updateFromJson(userData);
    }

    return user;
  };

  /** Create a fresh User from JSON without touching the shared cache.
   * Use this for event snapshots (upgrade requests) that should not
   * overwrite the canonical cached user object.
   */
  createUserFromJson = (userData: UserJson): User => {
    return new User({
      userData,
      envService: this.envService,
      rdmApiService: this.rdmApiService,
      globalErrorService: this.globalErrorService,
      confirmationService: this.confirmationService,
    });
  };

  getUser = (email: string): User | null => {
    return this.users[email] || null;
  };

  forgotPassword = async ({ email }: { email: string }): Promise<void> => {
    try {
      await Auth.forgotPassword(email);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      if (error.code === 'NotAuthorizedException') {
        await this.rdmApiService.axios.get(`/resend-temp-password/${email}`);
      } else {
        throw error;
      }
    }
  };

  resetPassword = async ({
    email,
    code,
    newPassword,
  }: {
    email: string;
    code: string;
    newPassword: string;
  }): Promise<void> => {
    await Auth.forgotPasswordSubmit(email, code, newPassword);
  };

  getLogInUrl = ({
    nextUrl,
    location,
  }: {
    nextUrl?: string;
    location: Location;
  }): string => {
    const currentUrl = getCurrentUrl(location);
    if (!nextUrl) {
      nextUrl = currentUrl;
    }

    const loginUrl = `${this.logInUrl}?next=${encodeURIComponent(nextUrl)}`;

    return `/disclaimer?urlToContinue=${encodeURI(
      loginUrl
    )}&urlToGoBack=${encodeURI(currentUrl)}`;
  };

  getAwsCredentials = (): void => {
    this.rdmApiService.axios.get('/get-aws-credentials');
  };

  private search = async (): Promise<void> => {
    const { text, limit, paginationToken } = this.searchState;
    const currentRequestNumber = this.searchState.currentRequestNumber + 1;
    runInAction(() => {
      this.searchState.results = undefined;
      this.searchState.currentRequestNumber = currentRequestNumber;
    });
    const resp = await this.rdmApiService.axios.get('search-users', {
      params: {
        text,
        limit,
        pagination_token: paginationToken,
      },
    });
    runInAction(() => {
      // This is needed so that stale results don't overwrite the current results
      if (this.searchState.currentRequestNumber === currentRequestNumber) {
        this.searchState.paginationToken = resp.data.pagination_token;
        this.searchState.results = resp.data.map((userJson: UserJson) => {
          return this.cacheUserIfNeeded(userJson);
        });
      }
    });
  };

  nextSearchPage = async (): Promise<void> => {
    if (!this.searchState.paginationToken) {
      // no more pages
      console.error('No more pages to load');
      return;
    }
    this.search();
  };

  fetchUser = async (email: string): Promise<void> => {
    try {
      const resp = await this.rdmApiService.axios.get(`/get-user/${email}`);
      const user = resp.data;
      this.cacheUserIfNeeded(user);
    } catch (error: unknown) {
      this.globalErrorService.showError({
        message: getErrorMessage({
          error,
          defaultMessage: 'Could not fetch user',
        }),
      });
    }
  };

fetchPendingApprovalUsers = async (): Promise<User[]> => {
  try {
    const response = await this.rdmApiService.axios.get('/get-pending-approval-users');
    const users = response.data?.items ?? response.data;
    if (Array.isArray(users)) {
      return users.map((userData: UserJson) => this.cacheUserIfNeeded(userData));
    }
    return [];
  } catch (error: unknown) {
    this.globalErrorService.showError({
      message: 'Could not fetch users pending approval',
    });
    return [];
  }
};

fetchAllPendingUpgrades = async (): Promise<User[]> => {
  try {
    const response = await this.rdmApiService.axios.get('/get-pending-upgrade-users');
    const users = response.data?.items ?? response.data;
    if (Array.isArray(users)) {
      return users.map((userData: UserJson) => this.createUserFromJson(userData));
    }
    return [];
  } catch (error: unknown) {
    this.globalErrorService.showError({
      message: 'Could not fetch pending upgrade requests',
    });
    return [];
  }
};

fetchAllPendingRequests = async (): Promise<User[]> => {
  try {
    const response = await this.rdmApiService.axios.get('/get-all-pending-requests');
    const users = response.data?.items ?? response.data;
    if (Array.isArray(users)) {
      return users.map((userData: UserJson) => this.createUserFromJson(userData));
    }
    return [];
  } catch (error: unknown) {
    this.globalErrorService.showError({
      message: 'Could not fetch pending user requests',
    });
    return [];
  }
};

fetchAllResolvedUpgradeRequests = async (): Promise<User[]> => {
  try {
    const response = await this.rdmApiService.axios.get('/get-all-resolved-upgrade-requests');
    const users = response.data?.items ?? response.data;
    if (Array.isArray(users)) {
      return users.map((userData: UserJson) => this.createUserFromJson(userData));
    }
    return [];
  } catch (error: unknown) {
    this.globalErrorService.showError({
      message: 'Could not fetch resolved upgrade requests',
    });
    return [];
  }
};

fetchRecentlyApprovedUsers = async (): Promise<User[]> => {
  try {
    const response = await this.rdmApiService.axios.get('/get-recently-approved-users');
    const users = response.data;
    const recentlyApprovedUsers: User[] = [];
    if (Array.isArray(users)) {
      users.forEach((userData: UserJson) => {
        const user = this.createUserFromJson(userData);
        recentlyApprovedUsers.push(user);
      });
    }
    return recentlyApprovedUsers;
  } catch (error: unknown) {
    this.globalErrorService.showError({
      message: 'Could not fetch recently approved users',
    });
    return [];
  }
};

fetchAllUsers = async (): Promise<User[]> => {
  try {
    const response = await this.rdmApiService.axios.get('/get-all-users');
    const users = response.data;
    this.users = {};
    if (Array.isArray(users)) {
      return users.map((userData: UserJson) => this.cacheUserIfNeeded(userData));
    }
    return [];
  } catch (error: unknown) {
    this.globalErrorService.showError({
      message: 'Could not fetch all users in user pool',
    });
    return [];
  }
};

adminUpdateMembershipTier = async (
  user: User,
  tier: string | undefined,
  comment?: string
): Promise<void> => {
  await this.rdmApiService.axios.post(
    `/admin-tier-update/${encodeURIComponent(user.email)}`,
    {
      new_tier: tier,
      current_user_tier: user.membershipTier,
      comments: comment,
      ...(tier === 'Tier 3' && user.orcidId ? { orcid_id: user.orcidId } : {}),
    },
    { headers: { 'X-CSRFToken': this.csrfToken } }
  );
};

postAdminComment = async (user: User, comment: string): Promise<void> => {
  await this.rdmApiService.axios.post(
    `/admin-comment/${encodeURIComponent(user.email)}`,
    {
      comments: comment,
      ...(user.membershipTier ? { current_user_tier: user.membershipTier } : {}),
    },
    { headers: { 'X-CSRFToken': this.csrfToken } }
  );
};

fetchLatestAdminComments = async (): Promise<Record<string, string>> => {
  try {
    const response = await this.rdmApiService.axios.get('/get-latest-admin-comments');
    return response.data ?? {};
  } catch {
    return {};
  }
};

enableUserAccess = async (user: User, comment?: string): Promise<void> => {
  await this.rdmApiService.axios.post(
    `/approve-user/${encodeURIComponent(user.email)}`,
    { comments: comment, event_type: 'ACCOUNT_ENABLED' },
    { headers: { 'X-CSRFToken': this.csrfToken } }
  );
};

restrictUserAccess = async (user: User, comment?: string): Promise<void> => {
  await this.rdmApiService.axios.post(
    `/deny-user/${encodeURIComponent(user.email)}`,
    { comments: comment, event_type: 'ACCOUNT_DISABLED' },
    { headers: { 'X-CSRFToken': this.csrfToken } }
  );
};

  _searchUsers = async (text?: string): Promise<void> => {
    this.searchState.text = text || null;
    this.searchState.paginationToken = null;
    this.search();
  };

  searchUsers = throttle(this._searchUsers, 700, {
    trailing: true,
  });

  requestTierUpgrade = async ({
    requestedTier,
    justification,
    orcidId,
    currentTier,
  }: {
    requestedTier: string;
    justification: string;
    orcidId?: string;
    currentTier?: string;
  }): Promise<void> => {
    if (!this.user) {
      throw new Error('Must be logged in to request a tier upgrade');
    }
    await this.rdmApiService.axios.post(
      `/upgrade-request/${encodeURIComponent(this.user.email)}`,
      {
        requested_tier: requestedTier,
        comments: justification,
        orcid_id: orcidId,
        current_user_tier: currentTier,
        performed_by: this.user.email,
        performed_by_type: 'USER',
      },
      { headers: { 'X-CSRFToken': this.csrfToken } }
    );
    // Optimistically update local user state so the UI reflects the pending request immediately
    if (this.user) {
      this.user.requestStatus = 'UPGRADE_REQUEST_PENDING';
    }
  };

  approve_new_user = async (user: User, comment?: string, assignedTier?: string): Promise<string> => {
    await this.rdmApiService.axios.post(
      `/approve-user/${encodeURIComponent(user.email)}`,
      {
        comments: comment || '',
        ...(assignedTier ? { assigned_tier: assignedTier } : {}),
      },
      { headers: { "X-CSRFToken": this.csrfToken } }
    );
    user.changeApproval(true);
    return 'User has been approved. If this was a mistake, refresh the page and make the appropriate selection.';
  };

  deny_new_user = async (user: User, comment?: string): Promise<string> => {
    await this.rdmApiService.axios.post(
      `/deny-user/${encodeURIComponent(user.email)}`,
      { comments: comment || '' },
      { headers: { "X-CSRFToken": this.csrfToken } }
    );
    user.changeApproval(false);
    return 'User has been denied. If this was a mistake, refresh the page and make the appropriate selection.';
  };
}
